import React, { useEffect, useState } from 'react';

import AdditionModal from '../../../components/AdditionModal';
import Button from '../../../components/Button';
import ContentWrapper from '../../../components/ContentWrapper';
import UsersList from '../../../components/UsersList';
import {
  useAddGroupMemberMutation,
  useRemoveGroupMemberMutation,
} from '../../../services/permissions';
import { PermissionGroup, PermissionGroupUser } from '../../../services/permissions/types';
import useError from '../../../utils/react/hooks/useError';
import useSearchStr from '../../../utils/react/hooks/useSearchStr';
import useSuccess from '../../../utils/react/hooks/useSuccess';
import useUsersQuery from '../../../utils/react/hooks/useUsersQuery';

interface MembersContentProps {
  group: PermissionGroup | undefined,
  groupId: string | undefined,
}

const MembersContent = ({ group, groupId }: MembersContentProps) => {
  // Search for users
  const [searchStr, setSearchStr] = useSearchStr('');

  // Filtered members
  const [users, setUsers] = useState<PermissionGroupUser[]>([]);

  useEffect(() => {
    if (group?.users) {
      setUsers(group.users.filter((u) => u.name.toLowerCase().includes(searchStr.toLowerCase())));
    }
  }, [group, searchStr]);

  const [targetMemberId, setTargetMemberId] = useState<string | undefined>(undefined);

  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [addMemberModalSearchStr, setAddMemberModalSearchStr] = useState('');

  const [addMember, addMemberOpts] = useAddGroupMemberMutation();
  const [removeMember, removeMemberOpts] = useRemoveGroupMemberMutation();
  const { data: searchRes, isFetching: isSearchResFetching } = useUsersQuery(
    { search: addMemberModalSearchStr },
    { skip: addMemberModalSearchStr === '' || !addMemberModalOpen },
  );

  useSuccess(removeMemberOpts.isSuccess, 'Member removed');
  useError(removeMemberOpts.isError, removeMemberOpts.error);

  return (
    <>
      <ContentWrapper title="Group Members">
        <UsersList
          data={users || []}
          columns={[{
            field: 'name',
            headerName: 'Name',
            width: 200,
          },
          {
            field: 'control',
            headerName: 'Control',
            width: 100,
            renderCell: ({ row }: any) => (
              <Button
                size="small"
                color="error"
                variant="text"
                allCaps
                disabled={removeMemberOpts.isLoading}
                loading={removeMemberOpts.isLoading && targetMemberId === row.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  setTargetMemberId(row.id);
                  removeMember({
                    group: groupId || '',
                    userId: row.id,
                  });
                }}
              >
                Remove
              </Button>
            ),
          }]}
          loading={!group?.users}
          onSearch={(s) => setSearchStr(s)}
          getRowHref={(u) => `/admin/${u.type === 'STUDENT' ? 'student' : 'staff'}/${u.id}`}
          onAddClick={() => setAddMemberModalOpen(true)}
        />
      </ContentWrapper>
      <AdditionModal
        title="Add Member"
        searchPlaceholder="Member"
        open={addMemberModalOpen}
        handleClose={() => {
          setAddMemberModalOpen(false);
        }}
        onAdd={async (u) => addMember({
          group: groupId || '',
          userId: u.id,
        })}
        isLoading={addMemberOpts.isLoading}
        isSuccess={addMemberOpts.isSuccess}
        isError={addMemberOpts.isError}
        error={(addMemberOpts.error as any)?.data?.message}
        closeNotifs={addMemberOpts.reset}
        searchFieldProps={{
          options: (searchRes || []).filter((x) => !(group?.users || [])
            .map((u) => u.value).includes(x?.id)),
          getOptionLabel: (u) => u.name,
          getOptionKey: (u) => u.id,
          onSearch: (s) => setAddMemberModalSearchStr(s),
          loading: isSearchResFetching,
        }}
      />
    </>
  );
};

export default MembersContent;
